import { xpectoToInvestmentClassMapping } from "common/backend/types/user-scoring";

import {
  AssetTypes,
  AverageYearlyTransactionsType,
  AverageYearlyVolumeType,
  EducationLevel,
  ExperienceLevel,
  Gender,
  IdTypes,
  LegalEntityType,
  PaymentMethodType,
  SaleInterestPayoutPeriod,
  XpectoInvestmentType
} from "../../backend/types/utils";
import i18n from "../../services/i18n";
import { defaultCountryAutocompleteOption } from "../../utils/utility";
import { countryList } from "./countryList";

const CountriesOptions = (): any => {
  let options = countryList("eu");
  options.push(defaultCountryAutocompleteOption);
  return options;
};

export const AutocompleteOptions = () => {
  return {
    countries: CountriesOptions(),
    educationLevel: [
      {
        label: i18n.t("Primary education"),
        code: EducationLevel.PRIMARY_EDUCATION
      },
      {
        label: i18n.t("Secondary education"),
        code: EducationLevel.SECONDARY_EDUCATION
      },
      {
        label: i18n.t("Academic degree"),
        code: EducationLevel.ACADEMIC_DEGREE
      },
      {
        label: i18n.t("Professional development"),
        code: EducationLevel.PROFESSIONAL_DEVELOPMENT
      },
      {
        label: i18n.t("Other"),
        code: EducationLevel.OTHER
      },
    ],
  };
};

export const SelectOptions = () => {
  const simpleOptions = {
    asset: [
      {
        label: i18n.t("Real Estate"),
        value: AssetTypes.REAL_ESTATE
      },
      {
        label: i18n.t("Loan"),
        value: AssetTypes.LOAN
      },
    ],
    experience: [
      {
        label: i18n.t("Non"),
        value: ExperienceLevel.NONE
      },
      {
        label: i18n.t("Up to 3 years"),
        value: ExperienceLevel.LOW
      },
      {
        label: i18n.t("3 to 5 years"),
        value: ExperienceLevel.MEDIUM
      },
      {
        label: i18n.t("More than 5 years"),
        value: ExperienceLevel.HIGH
      },
    ],
    gender: [
      {
        label: i18n.t("Male"),
        value: Gender.MALE
      },
      {
        label: i18n.t("Female"),
        value: Gender.FEMALE
      },
    ],
    idDocumentType: [
      {
        label: i18n.t("ID Card"),
        value: IdTypes.ID
      },
      {
        label: i18n.t("Passport"),
        value: IdTypes.PASSPORT
      },
    ],
    legalEntityType: [
      {
        label: i18n.t("AG"),
        value: LegalEntityType.AG,
      },
      {
        label: i18n.t("GBR"),
        value: LegalEntityType.GBR,
      },
      {
        label: i18n.t("GMBH"),
        value: LegalEntityType.GMBH,
      },
      {
        label: i18n.t("KG"),
        value: LegalEntityType.KG,
      },
      {
        label: i18n.t("STIFTUNG"),
        value: LegalEntityType.STIFTUNG,
      },
      {
        label: i18n.t("UG"),
        value: LegalEntityType.UG,
      },
    ],
    xpectoInvestmentType: [
      {
        value: XpectoInvestmentType.ALTERNATIVE_INVESTMENT_FUND,
        label: i18n.t("Alternative investment funds")
      },
      {
        value: XpectoInvestmentType.BOND,
        label: i18n.t("Bonds")
      },
      {
        value: XpectoInvestmentType.CERTIFICATE,
        label: i18n.t("Certificates")
      },
      {
        value: XpectoInvestmentType.CONTRACT_FOR_DIFFERENCE,
        label: i18n.t("Contracts for difference")
      },
      {
        value: XpectoInvestmentType.CRYPTO_CURRENCY,
        label: i18n.t("Security tokens")
      },
      {
        value: XpectoInvestmentType.EMERGING_MARKET_BONDS,
        label: i18n.t("Emerging market bonds")
      },
      {
        value: XpectoInvestmentType.FIXED_DEPOSIT,
        label: i18n.t("Fixed deposits")
      },
      {
        value: XpectoInvestmentType.FUTURES_TRADING,
        label: i18n.t("Futures trading")
      },
      {
        value: XpectoInvestmentType.GOVERNMENT_BONDS,
        label: i18n.t("Government bonds")
      },
      {
        value: XpectoInvestmentType.HEDGE_FUND,
        label: i18n.t("Hedge funds")
      },
      {
        value: XpectoInvestmentType.INVESTMENT_FUND,
        label: i18n.t("Investment funds")
      },
      {
        value: XpectoInvestmentType.MONEY_MARKET_FUNDS,
        label: i18n.t("Money market funds")
      },
      {
        value: XpectoInvestmentType.OPERATIVE_PARTICIPATION_IN_A_LIMITED_PARTNERSHIP,
        label: i18n.t("Operative participations in a limited partnership")
      },
      {
        value: XpectoInvestmentType.PARTICIPATION_IN_A_LIMITED_PARTNERSHIP,
        label: i18n.t("Participations in a limited partnership")
      },
      {
        value: XpectoInvestmentType.PARTICIPATION_PAPER,
        label: i18n.t("Participation papers")
      },
      {
        value: XpectoInvestmentType.PARTICIPATION_RIGHT,
        label: i18n.t("Participation rights")
      },
      {
        value: XpectoInvestmentType.PRIVATE_EQUITY,
        label: i18n.t("Private equity")
      },
      {
        value: XpectoInvestmentType.REVERSE_CONVERTIBLE_BOND,
        label: i18n.t("Reverse convertible bonds")
      },
      {
        value: XpectoInvestmentType.SHARE,
        label: i18n.t("Shares")
      },
      {
        value: XpectoInvestmentType.UNSECURED_JUNIOR_DEBT,
        label: i18n.t("Unsecured junior debts")
      },
      {
        value: XpectoInvestmentType.WARRANTS,
        label: i18n.t("Warrants")
      },
    ],
    paymentMethodType: [
      {
        label: i18n.t("Credit Card"),
        value: PaymentMethodType.CREDIT_CARD,
        disabled: true
      },
      {
        label: i18n.t("SEPA Direct Debit"),
        value: PaymentMethodType.SEPA_DIRECT_DEBIT,
        disabled: true
      },
      {
        label: i18n.t("Wire Transfer"),
        value: PaymentMethodType.WIRE_TRANSFER
      },
    ],
    period: [
      {
        label: i18n.t("Quarterly"),
        value: SaleInterestPayoutPeriod.QUARTERLY
      },
      {
        label: i18n.t("Semi-Annually"),
        value: SaleInterestPayoutPeriod.BIANNUAL
      },
      {
        label: i18n.t("Annually"),
        value: SaleInterestPayoutPeriod.YEARLY
      },
    ],
    transactionsPerYear: [
      {
        label: i18n.t("None"),
        value: AverageYearlyTransactionsType.NONE
      },
      {
        label: i18n.t("Less or equal 5"),
        value: AverageYearlyTransactionsType.LOW
      },
      {
        label: i18n.t("Between 5 and 10"),
        value: AverageYearlyTransactionsType.MEDIUM
      },
      {
        label: i18n.t("More than 10"),
        value: AverageYearlyTransactionsType.HIGH
      },
    ],
    volumePerYear: [
      {
        label: i18n.t("Less or equal 3 thousand"),
        value: AverageYearlyVolumeType.LOW
      },
      {
        label: i18n.t("Between 3 and 5 thousand"),
        value: AverageYearlyVolumeType.MEDIUM
      },
      {
        label: i18n.t("Between 5 and 10 thousand"),
        value: AverageYearlyVolumeType.HIGH
      },
      {
        label: i18n.t("More than 10 thousand"),
        value: AverageYearlyVolumeType.VERY_HIGH
      },
    ],
    yesNo: [
      {
        label: i18n.t("Yes"),
        value: true
      },
      {
        label: i18n.t("No"),
        value: false
      },
    ],
  };

  const complexOptions = {
    investmentClass: simpleOptions.xpectoInvestmentType.filter((option: any) => xpectoToInvestmentClassMapping[option.value]).map((option: any) => ({
      ...option,
      value: xpectoToInvestmentClassMapping[option.value]
    }))
  };

  return Object.assign(
    simpleOptions,
    complexOptions
  );
};
